import Check from "@mui/icons-material/Check";
import ReaderModeOutlined from "@mui/icons-material/ChromeReaderModeOutlined";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import PictureAsPdfOutlined from "@mui/icons-material/PictureAsPdfOutlined";
import Search from "@mui/icons-material/Search";
import { Box, Grid, LinearProgress, Paper, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { FullScreenDocumentPreview, extensionFromName } from "components/Document/shared";
import { useCallback, useEffect, useMemo, useState } from "react";

import type { ScoutConversationPrivateDocumentRow } from "../hooks/useScoutConversationPrivateDocuments";

import { useScoutLargeDocumentExtractionProgress } from "../hooks/useScoutLargeDocumentExtractionProgress";
import {
  getScoutLargeDocumentExtractionProgressUi,
  type ScoutExtractionPipelineStage,
} from "./scoutLargeDocumentJobUi";

const TICK_INTERVAL_MS = 15_000;

function useTickingClock(intervalMs: number): number {
  const [clock, setClock] = useState(() => Date.now());
  useEffect(() => {
    const handle = window.setInterval(() => setClock(Date.now()), intervalMs);
    return () => window.clearInterval(handle);
  }, [intervalMs]);
  return clock;
}

function stageIcon(stage: ScoutExtractionPipelineStage) {
  const iconSx = { fontSize: 16 };

  switch (stage.id) {
    case "preview":
      return <ReaderModeOutlined sx={iconSx} />;
    case "extracting":
      return <Search sx={iconSx} />;
  }
}

function PipelineStageChip({ stage }: { stage: ScoutExtractionPipelineStage }) {
  const theme = useTheme();

  const palette = (() => {
    if (stage.state === "complete") {
      return {
        bgcolor: alpha(theme.palette.success.main, 0.12),
        borderColor: alpha(theme.palette.success.main, 0.35),
        color: theme.palette.success.dark,
      };
    }
    if (stage.state === "active") {
      return {
        bgcolor: alpha(theme.palette.primary.main, 0.08),
        borderColor: alpha(theme.palette.primary.main, 0.35),
        color: theme.palette.primary.main,
      };
    }
    if (stage.state === "failed") {
      return {
        bgcolor: alpha(theme.palette.error.main, 0.08),
        borderColor: alpha(theme.palette.error.main, 0.35),
        color: theme.palette.error.dark,
      };
    }
    return {
      bgcolor: alpha(theme.palette.text.primary, 0.04),
      borderColor: alpha(theme.palette.divider, 0.9),
      color: theme.palette.text.disabled,
    };
  })();

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 1.25,
        py: 0.5,
        borderRadius: 1.5,
        border: 1,
        borderColor: palette.borderColor,
        bgcolor: palette.bgcolor,
        color: palette.color,
        minWidth: 0,
        flex: "1 1 0",
      }}
    >
      {stageIcon(stage)}
      <Typography variant="caption" fontWeight={stage.state === "active" ? 600 : 500} noWrap>
        {stage.label}
      </Typography>

      <Grid flex={1} container justifyContent="flex-end">
        {stage.state === "complete" ? <Check sx={{ color: palette.color, fontSize: 16 }} /> : null}
        {stage.state === "failed" ? (
          <ErrorOutline sx={{ color: palette.color, fontSize: 16 }} />
        ) : null}
      </Grid>
    </Box>
  );
}

type Props = {
  tenantId?: string;
  conversationId?: string;
  privateDocumentId: string;
  documents: ScoutConversationPrivateDocumentRow[];
};

export function ScoutLargeDocumentExtractionProgressCard({
  tenantId,
  conversationId,
  privateDocumentId,
  documents,
}: Props) {
  const now = useTickingClock(TICK_INTERVAL_MS);
  const { document, extractionStartedAtMs, isVisible } = useScoutLargeDocumentExtractionProgress({
    tenantId,
    conversationId,
    privateDocumentId,
    documents,
  });

  if (!isVisible || !document) {
    return null;
  }

  const ui = getScoutLargeDocumentExtractionProgressUi(document, {
    startedAtMs: extractionStartedAtMs,
    now,
  });

  return <ScoutLargeDocumentExtractionProgressCardContent document={document} ui={ui} />;
}

type ScoutLargeDocumentExtractionProgressCardContentProps = {
  document: ScoutConversationPrivateDocumentRow;
  ui: ReturnType<typeof getScoutLargeDocumentExtractionProgressUi>;
};

function ScoutLargeDocumentExtractionProgressCardContent({
  document,
  ui,
}: ScoutLargeDocumentExtractionProgressCardContentProps) {
  const theme = useTheme();
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const signedDownloadUrl = document.signedDownloadUrl ?? undefined;
  const canPreview = signedDownloadUrl !== undefined;
  const fileExtension = useMemo(() => extensionFromName(document.filename), [document.filename]);

  const openPreview = useCallback(() => {
    if (!canPreview) {
      return;
    }
    setIsPreviewOpen(true);
  }, [canPreview]);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-start",
        py: 1,
        px: { xs: 2, sm: 3 },
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          width: "100%",
          maxWidth: 720,
          borderRadius: 4,
          boxShadow: "none",
          overflow: "hidden",
        }}
      >
        <Stack spacing={1.5} sx={{ p: 2 }}>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="flex-start"
            onClick={canPreview ? openPreview : undefined}
            sx={{
              cursor: canPreview ? "pointer" : "default",
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 60,
                flexShrink: 0,
                borderRadius: 1,
                border: 1,
                borderColor: "divider",
                bgcolor: alpha(theme.palette.text.primary, 0.04),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}
            >
              <PictureAsPdfOutlined color="action" sx={{ fontSize: 28 }} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="subtitle2" fontWeight={700} noWrap title={document.filename}>
                {document.filename}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {ui.headlineStatus} {ui.startedAgo ? `started ${ui.startedAgo}` : null}
              </Typography>
            </Box>
          </Stack>

          {ui.pageProgressLabel ? (
            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
              <Typography variant="body2" fontWeight={600}>
                {ui.pageProgressLabel}
              </Typography>
              {ui.timeRemainingLabel ? (
                <Typography variant="body2" color="text.secondary">
                  {ui.timeRemainingLabel}
                </Typography>
              ) : null}
            </Stack>
          ) : null}

          {ui.showProgressBar ? (
            <LinearProgress
              variant={ui.progressVariant}
              value={ui.progressValue}
              sx={{
                height: 8,
                borderRadius: 1,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                "& .MuiLinearProgress-bar": {
                  borderRadius: 1,
                },
              }}
            />
          ) : null}

          <Stack direction="row" spacing={0.75} sx={{ flexWrap: { xs: "wrap", sm: "nowrap" } }}>
            {ui.pipelineStages.map((stage) => (
              <PipelineStageChip key={stage.id} stage={stage} />
            ))}
          </Stack>
        </Stack>
      </Paper>
      {signedDownloadUrl ? (
        <FullScreenDocumentPreview
          open={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
          fileUrl={signedDownloadUrl}
          fileExtension={fileExtension}
          title={document.filename}
        />
      ) : null}
    </Box>
  );
}
